import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Calendar, CheckCircle, MessageSquare, Shield, 
  Star, ArrowRight, Users, Building, Heart 
} from 'lucide-react';
import { Button } from '../components/ui/button';

const steps = [
  {
    icon: Search,
    title: 'Search & Discover',
    description: 'Browse verified listings by location, type, and amenities. Filter by stalls, pastures, layover spots, or full boarding facilities.',
    forGuest: true,
  },
  {
    icon: Building,
    title: 'Create Your Listing',
    description: 'Set up your barn or pasture listing with photos, pricing, and availability. Our team reviews each listing for quality assurance.',
    forHost: true,
  },
  {
    icon: Calendar,
    title: 'Request to Book',
    description: 'Found the perfect spot? Send a booking request with your dates, number of animals, and a message to the host.',
    forGuest: true,
  },
  {
    icon: CheckCircle,
    title: 'Accept Bookings',
    description: 'Review incoming requests, check guest profiles, and accept or decline within your response window.',
    forHost: true,
  },
  {
    icon: MessageSquare,
    title: 'Connect & Communicate',
    description: 'Message directly with hosts or guests to discuss details, special requirements, and coordinate arrivals.',
    forGuest: true,
    forHost: true,
  },
  {
    icon: Shield,
    title: 'Secure Payments',
    description: 'Payments are processed securely. Hosts receive payouts after successful stays, with dispute protection for both parties.',
    forGuest: true,
    forHost: true,
  },
  {
    icon: Star,
    title: 'Review & Build Trust',
    description: 'After each stay, both guests and hosts can leave reviews, building a trusted community of animal lovers.',
    forGuest: true,
    forHost: true,
  },
];

const benefits = {
  guest: [
    { title: 'Verified Hosts', desc: 'Every listing is reviewed and approved' },
    { title: 'Secure Booking', desc: 'Protected payments and dispute resolution' },
    { title: 'Direct Communication', desc: 'Message hosts before and during your stay' },
    { title: 'Flexible Options', desc: 'From overnight layovers to full boarding' },
  ],
  host: [
    { title: 'Extra Income', desc: 'Monetize your unused barn or pasture space' },
    { title: 'Control Your Schedule', desc: 'Set your own availability and pricing' },
    { title: 'Trusted Guests', desc: 'Review guest profiles before accepting' },
    { title: 'Payout Protection', desc: 'Secure payments deposited to your account' },
  ],
};

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-farmhouse-charcoal" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="pt-16 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-4">
            How It Works
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-farmhouse-cream mb-6">
            Simple & Secure Boarding
          </h1>
          <p className="text-xl text-farmhouse-muted max-w-2xl mx-auto">
            Whether you're looking for a safe place for your horses or want to share your 
            barn space, we've made the process easy and secure.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-farmhouse-paper">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Guest Steps */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 rounded-md bg-farmhouse-saddle/20">
                  <Users className="w-6 h-6 text-farmhouse-saddle" />
                </div>
                <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream">
                  For Guests
                </h2>
              </div>
              <div className="space-y-8">
                {steps.filter(s => s.forGuest).map((step, index) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="shrink-0">
                      <div className="w-12 h-12 rounded-full bg-farmhouse-saddle/10 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-farmhouse-saddle" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-semibold text-farmhouse-cream mb-2">
                        {step.title}
                      </h3>
                      <p className="text-farmhouse-muted leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <Link to="/search">
                  <Button className="btn-primary">
                    Find a Stable
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Host Steps */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 rounded-md bg-farmhouse-moss/20">
                  <Building className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream">
                  For Hosts
                </h2>
              </div>
              <div className="space-y-8">
                {steps.filter(s => s.forHost).map((step, index) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="shrink-0">
                      <div className="w-12 h-12 rounded-full bg-farmhouse-moss/10 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-green-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-semibold text-farmhouse-cream mb-2">
                        {step.title}
                      </h3>
                      <p className="text-farmhouse-muted leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <Link to="/auth?role=host">
                  <Button className="bg-farmhouse-moss hover:bg-farmhouse-moss/80 text-white">
                    Become a Host
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-farmhouse-charcoal">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-3">
              Benefits
            </p>
            <h2 className="font-serif text-4xl font-semibold text-farmhouse-cream">
              Why Choose Barns & Pastures?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Guest Benefits */}
            <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-8">
              <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-6 flex items-center gap-2">
                <Heart className="w-5 h-5 text-farmhouse-saddle" />
                Guest Benefits
              </h3>
              <div className="space-y-4">
                {benefits.guest.map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-farmhouse-saddle shrink-0 mt-0.5" />
                    <div>
                      <p className="text-farmhouse-cream font-medium">{benefit.title}</p>
                      <p className="text-farmhouse-muted text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Host Benefits */}
            <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-8">
              <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-6 flex items-center gap-2">
                <Building className="w-5 h-5 text-green-400" />
                Host Benefits
              </h3>
              <div className="space-y-4">
                {benefits.host.map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-farmhouse-cream font-medium">{benefit.title}</p>
                      <p className="text-farmhouse-muted text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-farmhouse-paper">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-farmhouse-muted mb-8">
            Join our community of horse owners and trusted hosts today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button className="btn-primary px-8">
                Find Stables
              </Button>
            </Link>
            <Link to="/auth?role=host">
              <Button variant="outline" className="btn-secondary px-8">
                List Your Space
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
